<script lang="ts" setup>
import { computed, ref } from 'vue';
import { VDialog } from 'vuetify/components/VDialog';
import AutomatedInterviewTriggerForm from './InterviewSchedulingStartForm.vue';
import InterviewSchedulingPreemptionWarningModal from './InterviewSchedulingPreemptionWarningModal.vue';
import InterviewSchedulingService from '@/core/shared/interview-scheduling/interview-scheduling.service';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { getUserName } from '@/core/shared/user-profile/utils';
import type { InterviewSchedulingRequestDto } from '@/core/shared/interview-scheduling/types/interview-scheduling.type';

const props = defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
}>();

const interviewSchedulingService = new InterviewSchedulingService();
const proProfileService = new ProProfileService();
const conversationIndexService = new ConversationIndexService();
const isDoNotContact = computed(() => conversationIndexService.isMessagingDoNotContact);
const doNotContactMessage =
  'This candidate is marked Do Not Contact. Remove the mark before scheduling an interview.';

const isLoading = ref(false);
const showWarningModal = ref(false);
const showFormDialog = ref(true);
const pendingFormData = ref<InterviewSchedulingRequestDto | null>(null);
const formInitialData = ref<InterviewSchedulingRequestDto>();

const candidateName = computed(() =>
  proProfileService.selectedProProfile ? getUserName(proProfileService.selectedProProfile) : '',
);

const handleClose = () => {
  interviewSchedulingService.resetValidationErrors();
  showFormDialog.value = true;
  showWarningModal.value = false;
  pendingFormData.value = null;
  formInitialData.value = undefined;
  emit('update:open', false);
};

const handleFormDataChange = (data: InterviewSchedulingRequestDto) => {
  pendingFormData.value = structuredClone(data);
};

const handleSubmit = async () => {
  if (!pendingFormData.value) return;
  if (isDoNotContact.value) {
    showWarningModal.value = false;
    showFormDialog.value = true;
    SnackbarService.criticalAlt(doNotContactMessage);
    return;
  }

  const isValid = interviewSchedulingService.validateInterviewSettings({
    enabled: true,
    contacts: pendingFormData.value.contacts,
    type: pendingFormData.value.type,
    customMeetingLink: pendingFormData.value.customMeetingLink,
    duration: pendingFormData.value.duration,
    venueDetails: pendingFormData.value.venueDetails || '',
    additionalDetails: pendingFormData.value.additionalDetails || '',
    schedulingPreferences: pendingFormData.value.schedulingPreferences || '',
    requireFinalConfirmation: pendingFormData.value.requireFinalConfirmation ?? true,
    scoreThreshold: 0,
    recruiterEmail: '',
    recruiterName: '',
    recruiterUserProfileId: null,
  });

  if (!isValid) {
    return;
  }

  isLoading.value = true;
  try {
    // Check if there's an ongoing interview thread and show a warning if so
    await interviewSchedulingService.updateOngoingInterviewThreadCheck();

    // DNC can change while the lookup is pending, including on preemption confirmation.
    if (isDoNotContact.value) {
      showWarningModal.value = false;
      showFormDialog.value = true;
      SnackbarService.criticalAlt(doNotContactMessage);
      return;
    }

    if (interviewSchedulingService.hasOngoingInterviewThread && !showWarningModal.value) {
      isLoading.value = false;
      // Keep the remount data separate from the form's initial dataChange emission.
      formInitialData.value = pendingFormData.value;
      showFormDialog.value = false;
      showWarningModal.value = true;
      return;
    }

    await submitInterviewSchedulingRequest();
    handleClose();
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.criticalAlt(
      error instanceof Error && error.message
        ? `${error.message}. Please try again later`
        : 'Failed to start interview scheduling, please try again in a few minutes.',
    );
  } finally {
    isLoading.value = false;
  }
};

const handleWarningCancel = () => {
  showWarningModal.value = false;
  // Keep modal closed, don't reopen form dialog
  handleClose();
};

const submitInterviewSchedulingRequest = async () => {
  if (!proProfileService.selectedProApplication?.id || !pendingFormData.value) {
    throw new Error('Application not found. Try to refresh the page and try again.');
  }

  await interviewSchedulingService.sendInterviewSchedulingRequest(
    proProfileService.selectedProApplication.id,
    pendingFormData.value,
  );
  await interviewSchedulingService.updateOngoingInterviewThreadCheck();
};
</script>

<template>
  <div>
    <!-- Form Dialog -->
    <v-dialog
      :model-value="props.open && showFormDialog"
      @update:model-value="$event ? null : handleClose()"
      max-width="900"
      scrollable
    >
      <div class="overflow-y-auto rounded-lg bg-white p-8 text-base shadow-md">
        <div class="mb-6 font-serif text-2xl font-black text-shade-900">
          Schedule Interview with {{ candidateName }}
        </div>
        <AutomatedInterviewTriggerForm
          :initial-data="formInitialData"
          @dataChange="handleFormDataChange"
          :isSubmitting="isLoading"
        />

        <p v-if="isDoNotContact" data-test="interview-scheduling-dnc" class="mt-4 text-shade-700">
          {{ doNotContactMessage }}
        </p>

        <div class="relative bottom-0 mt-6 flex flex-row justify-end">
          <v-btn
            :ripple="false"
            class="modal-button-secondary"
            variant="flat"
            :loading="isLoading"
            :disabled="isLoading"
            @click="handleClose"
          >
            Cancel
          </v-btn>
          <v-btn
            data-test="interview-scheduling-submit"
            :ripple="false"
            class="modal-button-primary ml-4"
            :loading="isLoading"
            :disabled="isLoading || isDoNotContact"
            variant="flat"
            @click="handleSubmit"
          >
            Let your AI recruiter schedule
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <InterviewSchedulingPreemptionWarningModal
      :is-open="showWarningModal"
      :is-loading="isLoading"
      :candidate-name="candidateName"
      @save="handleSubmit"
      @cancel="handleWarningCancel"
    />
  </div>
</template>

<style scoped>
.modal-button-primary {
  @apply bg-highlight-500 text-white hover:bg-highlight-600;
}

.modal-button-secondary {
  @apply bg-white text-shade-880 hover:bg-tint-60;
}
</style>
